// VTA — browser bootstrap.
//
// All real logic lives in ./js/*.js modules. This file only:
// - asserts required DOM ids exist;
// - resolves the runtime configuration (apiBaseUrl, wsUrl);
// - fetches /config and /workflows so the form is pre-filled;
// - constructs one LiveSession;
// - wires DOM event listeners.

import {
  applyAiNoticeI18n,
  ensureAiNoticeAcknowledged,
  resetAiNoticeAcknowledgement,
} from "./js/ai-notice.js";
import { ensureRuntimeConfig } from "./js/runtime-config.js";
import {
  AUTO_CHECK_INTERVAL_MS,
  DEFAULT_WORKFLOW_ID,
  FRAME_INTERVAL_MS,
  FRAME_MAX_DIM,
  RECV_RATE,
  SEND_RATE,
} from "./js/constants.js";
import {
  clearDebugLog,
  logDebug,
  rerenderDebugLog,
  setDebugWsState,
  updateDebugCounters,
} from "./js/debug.js";
import { els, ensureRequired } from "./js/dom.js";
import {
  getLanguage,
  onLanguageChange,
  setLanguage,
  SUPPORTED_LANGUAGES,
  t,
} from "./js/i18n.js";
import { LiveSession } from "./js/live-session.js";
import { RecorderController } from "./js/recorder.js";
import {
  appendMessage,
  renderStartStopButton,
  setState,
  setWorkflowControlsEnabled,
} from "./js/state.js";
import { scheduleCurrentStepFit } from "./js/text-fit.js";
import {
  preloadWorkflowImagesInOrder,
  renderWorkflowState,
  selectedWorkflowId,
} from "./js/workflow-ui.js";
import {
  fetchConfig,
  fetchWorkflow,
  fetchWorkflows,
} from "./js/workflow-client.js";
import {
  attemptLogin,
  attemptLogout,
  loadCurrentSession,
  showLogin,
} from "./js/auth-ui.js";
import { UnauthorizedError } from "./js/api-client.js";

ensureRequired();

// ---- bootstrap fetches ------------------------------------------------------

async function bootstrap() {
  try {
    // Resolve runtime config first; everything else (HTTP fetches and the
    // WebSocket URL) reads from this resolution.
    const runtimeCfg = await ensureRuntimeConfig();
    logDebug("browser", "runtime_config_loaded", {
      api_base_url: runtimeCfg.apiBaseUrl,
      ws_url: runtimeCfg.wsUrl,
      frontend_name: runtimeCfg.frontendName,
      frontend_version: runtimeCfg.frontendVersion,
      protocol_version: runtimeCfg.protocolVersion,
      source: runtimeCfg._source,
    });

    // Auth gate: every protected backend call requires a session cookie. We
    // ask the server first, render the login overlay if needed, and only
    // start fetching config/workflows once we know we're signed in. This
    // is purely UX — the server independently rejects unauthenticated
    // requests.
    const session = await loadCurrentSession();
    if (!session) {
      // Login form will run loadAuthenticatedState() on submit.
      return;
    }
    // EU AI Act transparency: show the AI-assisted-processing notice before
    // the camera/Live API controls become usable, once per session.
    await ensureAiNoticeAcknowledged();
    await loadAuthenticatedState();
  } catch (e) {
    if (e instanceof UnauthorizedError) {
      showLogin();
      return;
    }
    console.warn("could not bootstrap", e);
  }
}

async function loadAuthenticatedState() {
  try {
    const cfg = await fetchConfig();

    // Authenticated, per-space capability: gate the local-video-source option
    // on the backend flag. Default off so other spaces never see it.
    const live = ensureSession();
    if (live.videoSources?.setDebugVideoSourcesEnabled) {
      live.videoSources.setDebugVideoSourcesEnabled(
        cfg.features?.debug_video_sources === true,
      );
    }

    els.voiceSel.innerHTML = "";
    for (const v of cfg.voices) {
      const opt = document.createElement("option");
      opt.value = v;
      opt.textContent = v;
      els.voiceSel.appendChild(opt);
    }
    els.voiceSel.value = cfg.default_voice;

    els.thinkingLevelSel.innerHTML = "";
    for (const level of cfg.thinking_levels) {
      const opt = document.createElement("option");
      opt.value = level;
      opt.textContent = level;
      els.thinkingLevelSel.appendChild(opt);
    }
    els.thinkingLevelSel.value = cfg.default_thinking_level;
    if (els.includeThoughts) {
      els.includeThoughts.checked = !!cfg.default_include_thoughts;
    }
    if (els.debugMode) els.debugMode.value = "clean";
    if (els.debugMedia) els.debugMedia.checked = false;
    if (els.debugAutoscroll) els.debugAutoscroll.checked = true;

    try {
      const workflows = await fetchWorkflows();
      els.workflowSel.innerHTML = "";
      for (const wf of workflows) {
        const opt = document.createElement("option");
        opt.value = wf.file_id || wf.id;
        opt.textContent = wf.title || wf.id;
        els.workflowSel.appendChild(opt);
      }
      if (workflows.some((wf) => (wf.file_id || wf.id) === DEFAULT_WORKFLOW_ID)) {
        els.workflowSel.value = DEFAULT_WORKFLOW_ID;
      } else if (workflows.length > 0) {
        els.workflowSel.value = workflows[0].file_id || workflows[0].id;
      }

      if (els.workflowSel.value) {
        const workflowCfg = await fetchWorkflow(els.workflowSel.value);
        if (workflowCfg.workflow) {
          live.setWorkflowConfig(workflowCfg.workflow);
          void preloadWorkflowImagesInOrder(workflowCfg.workflow);
        }
        if (workflowCfg.active_step) renderWorkflowState(workflowCfg.active_step);
      }
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        await handleAuthLost();
        return;
      }
      console.warn("could not fetch workflows", e);
    }
  } catch (e) {
    if (e instanceof UnauthorizedError) {
      await handleAuthLost();
      return;
    }
    console.warn("could not fetch /config", e);
  }
}

async function handleAuthLost() {
  // Disconnect any live session and return to the login overlay. Called
  // whenever a protected API/WS call returns 401.
  try {
    if (session && session.ws) {
      await session.disconnect("session expired.", { stopMedia: false });
    }
  } catch (_) {}
  if (els.workflowSel) els.workflowSel.innerHTML = "";
  resetAiNoticeAcknowledgement();
  showLogin({ message: t("auth.session_expired") });
}

// ---- i18n -------------------------------------------------------------------
// Apply the current language to every static label in the page. Called once
// at boot (after `ensureRequired`) and again whenever the user changes the
// language switcher. Dynamic content (workflow state, recorder status,
// status lines) re-renders via its own handlers when language changes.

function applyStaticI18n() {
  const lang = getLanguage();

  // The connect button's textContent is owned by `renderStartStopButton`,
  // which already reads from i18n; call it so a language change updates
  // the visible label even when no other event fires.
  const isActive = els.connectBtn?.dataset.state === "active";
  renderStartStopButton(isActive);

  if (els.captureAudioBtn) {
    const audioActive = els.captureAudioBtn.dataset.active === "true";
    els.captureAudioBtn.textContent = audioActive
      ? t("controls.stop_audio")
      : t("controls.capture_audio");
  }
  if (els.addVideoSourceBtn) {
    els.addVideoSourceBtn.textContent = t("controls.add_video_source");
  }
  // Mic button has icon + label children — only the label text is translated.
  if (els.micBtn) {
    const label = els.micBtn.querySelector(".label");
    if (label) {
      // Muted/unmuted state is owned by the LiveSession; default static
      // label is "Unmute mic" because the button starts disabled+muted.
      const muted = (session?.mic?.muted ?? true);
      label.textContent = muted ? t("controls.unmute_mic") : t("controls.mute_mic");
    }
  }

  // Silent-mode (playback) toggle. State is owned by the LiveSession; default
  // is on (silent) until a workflow or the user changes it.
  if (els.silentModeBtn) {
    const silent = session?.silentMode ?? false;
    els.silentModeBtn.dataset.active = String(silent);
    els.silentModeBtn.title = t("controls.silent_mode_hint");
    els.silentModeBtn.textContent = silent
      ? t("controls.silent_mode_on")
      : t("controls.silent_mode_off");
  }

  // Language switcher label + selected value.
  if (els.languageSwitcherLabel) {
    els.languageSwitcherLabel.textContent = t("controls.language_label");
  }
  if (els.languageSelect && els.languageSelect.value !== lang) {
    els.languageSelect.value = lang;
  }

  // Workflow picker label (in `.workflow-picker > span`).
  const workflowPickerLabel = document.querySelector(".workflow-picker > span");
  if (workflowPickerLabel) {
    workflowPickerLabel.textContent = t("controls.workflow_label");
  }
  // Auth status label + sign-out button.
  const authStatusLabel = document.querySelector(".auth-status .auth-status-label");
  if (authStatusLabel) authStatusLabel.textContent = t("auth.signed_in_as");
  if (els.logoutBtn) els.logoutBtn.textContent = t("auth.sign_out");

  // Login overlay.
  const loginTitle = document.getElementById("login-title");
  if (loginTitle) loginTitle.textContent = t("auth.title");
  const loginSubtitle = document.querySelector("#login-overlay .login-card > p");
  if (loginSubtitle) loginSubtitle.textContent = t("auth.subtitle");
  const loginSpaceLabel = document.querySelector("#login-form label:nth-of-type(1) > span");
  if (loginSpaceLabel) loginSpaceLabel.textContent = t("auth.space_label");
  const loginCodeLabel = document.querySelector("#login-form label:nth-of-type(2) > span");
  if (loginCodeLabel) loginCodeLabel.textContent = t("auth.code_label");
  if (els.loginSubmit) els.loginSubmit.textContent = t("auth.submit");

  // AI-assisted image processing notice.
  applyAiNoticeI18n();

  // Recorder card.
  const recorderHeaderStrong = document.querySelector(".recorder-header > strong");
  if (recorderHeaderStrong) recorderHeaderStrong.textContent = t("recorder.title");
  const recorderDisclaimer = document.querySelector(".recorder-disclaimer");
  if (recorderDisclaimer) recorderDisclaimer.textContent = t("recorder.disclaimer");
  if (recorder) renderRecorderUi(recorder.statusPayload());

  // Step display defaults (only visible before any workflow_state arrives).
  const currentStepCardEmpty =
    !els.currentStepCard?.dataset.stepIndex ||
    els.currentStepCard.dataset.stepIndex === "0";
  if (currentStepCardEmpty) {
    if (els.currentStepCounter) els.currentStepCounter.textContent = t("step.counter_unknown");
    if (els.currentStepTitle) els.currentStepTitle.textContent = t("step.no_active");
    if (els.currentStepInstruction) {
      els.currentStepInstruction.textContent = t("step.connect_to_start");
    }
    if (els.workflowTitle) {
      els.workflowTitle.textContent = t("step.workflow_title_placeholder");
    }
    if (els.nextStepEyebrow) els.nextStepEyebrow.textContent = t("step.next_eyebrow");
    if (els.nextStepTitle) els.nextStepTitle.textContent = t("step.waiting");
    if (els.nextStepInstruction) {
      els.nextStepInstruction.textContent = t("step.next_placeholder");
    }
  }

  // Workflow nav buttons.
  if (els.prevStepIndicator) {
    const prevText = els.prevStepIndicator.querySelector("span:not([aria-hidden])");
    if (prevText) prevText.textContent = t("controls.prev");
  }
  if (els.nextStepIndicator) {
    const nextText = els.nextStepIndicator.querySelector("span:not([aria-hidden])");
    if (nextText) nextText.textContent = t("controls.next");
  }

  // Debug labels (visible whenever the debug panel is expanded; still cheap
  // to keep in sync even when collapsed).
  const debugHeaderTitle = document.querySelector(".debug-header h2");
  if (debugHeaderTitle) debugHeaderTitle.textContent = t("debug.title");
  const debugHeaderSubtitle = document.querySelector(".debug-header p");
  if (debugHeaderSubtitle) debugHeaderSubtitle.textContent = t("debug.subtitle");
  if (els.debugClear) els.debugClear.textContent = t("debug.clear");
  if (els.debugToggle) {
    const collapsed = els.layoutRoot?.classList.contains("debug-collapsed");
    els.debugToggle.textContent = collapsed ? t("debug.title") : t("debug.fold");
  }
  // Debug grid + setting labels.
  const debugStateLabels = document.querySelectorAll(".debug-state-grid > div > span");
  const stateKeys = ["debug.state", "debug.ws", "debug.audio_in", "debug.images", "debug.audio_out", "debug.errors"];
  debugStateLabels.forEach((el, i) => {
    if (stateKeys[i]) el.textContent = t(stateKeys[i]);
  });
  const debugSettingsLabels = document.querySelectorAll(".debug-settings > label > span");
  const settingKeys = ["debug.voice", "debug.thinking", "debug.thoughts"];
  debugSettingsLabels.forEach((el, i) => {
    if (settingKeys[i]) el.textContent = t(settingKeys[i]);
  });
  const debugLogLabel = document.querySelector(".debug-options > label > span");
  if (debugLogLabel) debugLogLabel.textContent = t("debug.log");
  if (els.debugMode) {
    for (const opt of els.debugMode.options) {
      if (opt.value === "clean") opt.textContent = t("debug.clean");
      else if (opt.value === "full") opt.textContent = t("debug.full_json");
    }
  }
  // Chat input placeholders.
  if (els.chatInput) els.chatInput.placeholder = t("debug.chat_locked_placeholder");
  if (els.chatSend) els.chatSend.textContent = t("debug.chat_send_locked");

  // "What the model sees" preview labels.
  const currentViewLabel = document.getElementById("debug-current-view-label");
  if (currentViewLabel) currentViewLabel.textContent = t("debug.current_view");
  const compositeViewLabel = document.getElementById("debug-composite-view-label");
  if (compositeViewLabel) compositeViewLabel.textContent = t("debug.composite_view");
  if (els.debugCurrentViewEmpty) {
    els.debugCurrentViewEmpty.textContent = t("debug.view_waiting");
  }
}

// ---- recorder ---------------------------------------------------------------
// Local-only recorder: produces a ZIP archive on the user's device. The
// recorder never touches the WebSocket, never makes fetch calls, and never
// uploads anything. See `js/recorder.js` for the privacy contract.

const recorder = new RecorderController({
  onStatusChange: (status) => renderRecorderUi(status),
});
renderRecorderUi(recorder.statusPayload());

function recorderButtonAction(state) {
  // The "arm" button doubles as "disarm" while armed.
  // The "free" button doubles as "stop free" while free-recording.
  const armDefault = t("recorder.arm");
  const freeDefault = t("recorder.start_free");
  if (state === "off") return { armLabel: armDefault, freeLabel: freeDefault };
  if (state === "workflow-armed") return { armLabel: t("recorder.disarm"), freeLabel: freeDefault };
  if (state === "workflow-recording") {
    return {
      armLabel: t("recorder.recording_workflow"),
      freeLabel: t("recorder.free_disabled_while_workflow"),
    };
  }
  if (state === "free-recording") {
    return {
      armLabel: t("recorder.workflow_disabled_while_free"),
      freeLabel: t("recorder.stop_free"),
    };
  }
  if (state === "finalizing") {
    return { armLabel: t("recorder.finalizing"), freeLabel: t("recorder.finalizing") };
  }
  return { armLabel: armDefault, freeLabel: freeDefault };
}

function renderRecorderUi(status) {
  if (els.recorderCard) {
    els.recorderCard.dataset.state = status.state;
  }
  if (els.recorderBadge) {
    const badge =
      status.state === "off" ? t("recorder.badge.off") :
      status.state === "workflow-armed" ? t("recorder.badge.armed") :
      status.state === "workflow-recording" ? t("recorder.badge.recording_workflow") :
      status.state === "free-recording" ? t("recorder.badge.recording_free") :
      status.state === "finalizing" ? t("recorder.badge.finalizing") :
      status.state === "error" ? t("recorder.badge.error") : status.state;
    els.recorderBadge.textContent = badge;
  }
  if (els.recorderStatus) {
    els.recorderStatus.textContent = status.message || "";
  }
  const labels = recorderButtonAction(status.state);
  if (els.recorderArmBtn) {
    els.recorderArmBtn.textContent = labels.armLabel;
    els.recorderArmBtn.dataset.active = String(status.state === "workflow-armed");
    els.recorderArmBtn.disabled =
      status.state === "workflow-recording" ||
      status.state === "free-recording" ||
      status.state === "finalizing";
  }
  if (els.recorderFreeBtn) {
    els.recorderFreeBtn.textContent = labels.freeLabel;
    els.recorderFreeBtn.dataset.active = String(status.state === "free-recording");
    els.recorderFreeBtn.disabled =
      status.state === "workflow-armed" ||
      status.state === "workflow-recording" ||
      status.state === "finalizing";
  }
}

async function onRecorderArmClick() {
  try {
    if (recorder.state === "workflow-armed") {
      recorder.disarmWorkflowCapture();
    } else if (recorder.state === "off") {
      recorder.armWorkflowCapture({ workflowId: selectedWorkflowId() });
    }
  } catch (e) {
    appendMessage("error", `recorder: ${e?.message || e}`);
    logDebug("browser", "recorder_button_failed", { which: "arm", message: e?.message || String(e) }, "warn");
  }
}

async function onRecorderFreeClick() {
  try {
    if (recorder.state === "free-recording") {
      await recorder.stopFreeCapture("user_stop");
    } else if (recorder.state === "off") {
      const live = ensureSession();
      await recorder.startFreeCapture({
        liveSession: live,
        videoSources: live.videoSources,
      });
    }
  } catch (e) {
    appendMessage("error", `recorder: ${e?.message || e}`);
    logDebug("browser", "recorder_button_failed", { which: "free", message: e?.message || String(e) }, "warn");
  }
}

// ---- session ----------------------------------------------------------------

let session = null;

function ensureSession() {
  if (!session) session = new LiveSession({ recorder });
  return session;
}

async function runMediaAction(action, errorLabel) {
  const live = ensureSession();
  try {
    await action(live);
  } catch (e) {
    console.error(e);
    appendMessage("error", `${errorLabel}: ${e.message || e}`);
    setState("error", errorLabel);
    logDebug(
      "browser",
      "media_action_error",
      { action: errorLabel, message: e.message || String(e) },
      "error",
    );
  } finally {
    if (session) session._renderMediaButtons();
  }
}

async function onCaptureAudioClick() {
  await runMediaAction(
    (live) => live.toggleAudio(),
    "failed to toggle audio capture",
  );
}

async function onConnectClick() {
  // EU AI Act transparency: never open the Live API connection (and start
  // sending camera frames to it) before the AI-processing notice has been
  // acknowledged this session.
  await ensureAiNoticeAcknowledged();

  const live = ensureSession();

  if (live.ws && live.workflowFinishedUi) {
    await live.disconnect("finished.", { stopMedia: false, stopPlayback: false });
  } else if (live.ws) {
    await live.disconnect(t("status.disconnected"), { stopMedia: false });
    return;
  }

  els.connectBtn.disabled = true;
  try {
    await live.connect({
      voice: els.voiceSel.value,
      thinkingLevel: els.thinkingLevelSel.value,
      includeThoughts: !!els.includeThoughts.checked,
    });
  } catch (e) {
    console.error(e);
    appendMessage("error", t("status.error_prefix", { msg: e.message || e }));
    setState("error", t("status.failed_to_connect"));
    if (session) await session.disconnect(t("status.failed_to_connect"), { stopMedia: false });
  } finally {
    els.connectBtn.disabled = false;
    renderStartStopButton(!!live.ws && !live.workflowFinishedUi);
  }
}

// ---- DOM wiring -------------------------------------------------------------

els.captureAudioBtn.addEventListener("click", onCaptureAudioClick);
els.connectBtn.addEventListener("click", onConnectClick);

if (els.micBtn) {
  els.micBtn.addEventListener("click", () => {
    if (session) session.toggleMic();
  });
}

if (els.silentModeBtn) {
  els.silentModeBtn.addEventListener("click", () => {
    ensureSession().toggleSilentMode();
  });
}

if (els.chatForm) {
  els.chatForm.addEventListener("submit", (e) => e.preventDefault());
}

if (els.debugClear) els.debugClear.addEventListener("click", clearDebugLog);
if (els.debugMode) els.debugMode.addEventListener("change", rerenderDebugLog);
if (els.debugMedia) els.debugMedia.addEventListener("change", rerenderDebugLog);

if (els.recorderArmBtn) els.recorderArmBtn.addEventListener("click", onRecorderArmClick);
if (els.recorderFreeBtn) els.recorderFreeBtn.addEventListener("click", onRecorderFreeClick);

if (els.debugToggle && els.layoutRoot) {
  els.debugToggle.addEventListener("click", () => {
    const collapsed = !els.layoutRoot.classList.contains("debug-collapsed");
    els.layoutRoot.classList.toggle("debug-collapsed", collapsed);
    els.debugToggle.textContent = collapsed ? t("debug.title") : t("debug.fold");
    els.debugToggle.setAttribute("aria-expanded", collapsed ? "false" : "true");
    scheduleCurrentStepFit();
    logDebug("browser", collapsed ? "debug_folded" : "debug_expanded");
  });
}

if (els.prevStepIndicator) {
  els.prevStepIndicator.addEventListener("click", () => {
    if (!session) return;
    session.sendStepInstruction("prev", { displayKind: "auto", allowDuringTurn: true });
  });
}

if (els.nextStepIndicator) {
  els.nextStepIndicator.addEventListener("click", () => {
    if (!session) return;
    session.sendStepInstruction("next", { displayKind: "auto", allowDuringTurn: true });
  });
}

if (els.loginForm) {
  els.loginForm.addEventListener("submit", async (e) => {
    e.preventDefault();
    if (els.loginSubmit) els.loginSubmit.disabled = true;
    if (els.loginError) {
      els.loginError.hidden = true;
      els.loginError.textContent = "";
    }
    try {
      await attemptLogin({
        space: els.loginSpace ? els.loginSpace.value : "",
        code: els.loginCode ? els.loginCode.value : "",
      });
      if (els.loginCode) els.loginCode.value = "";
      await ensureAiNoticeAcknowledged();
      await loadAuthenticatedState();
    } catch (err) {
      const message =
        (err && err.body && err.body.detail) ||
        err.message ||
        t("auth.title");
      if (els.loginError) {
        els.loginError.textContent = message;
        els.loginError.hidden = false;
      }
      logDebug("browser", "login_failed", { status: err && err.status }, "warn");
    } finally {
      if (els.loginSubmit) els.loginSubmit.disabled = false;
    }
  });
}

if (els.logoutBtn) {
  els.logoutBtn.addEventListener("click", async () => {
    els.logoutBtn.disabled = true;
    try {
      // Stop any active free recording locally first; the recorder's ZIP is
      // saved to the user's downloads, not uploaded anywhere. (Workflow
      // recording, if any, is finalized by session.disconnect below.)
      if (recorder && recorder.state === "free-recording") {
        try {
          await recorder.stopFreeCapture("logout");
        } catch (_) {
          /* finalize is best-effort on logout */
        }
      }
      if (session && session.ws) {
        await session.disconnect(t("auth.signed_out"), { stopMedia: false });
      }
      if (els.workflowSel) els.workflowSel.innerHTML = "";
      await attemptLogout();
      resetAiNoticeAcknowledgement();
    } finally {
      els.logoutBtn.disabled = false;
    }
  });
}

if (els.workflowSel) {
  els.workflowSel.addEventListener("change", async () => {
    try {
      const workflowCfg = await fetchWorkflow(selectedWorkflowId());
      const live = ensureSession();
      if (workflowCfg.workflow) {
        live.setWorkflowConfig(workflowCfg.workflow);
        void preloadWorkflowImagesInOrder(workflowCfg.workflow);
      }
      if (workflowCfg.active_step) renderWorkflowState(workflowCfg.active_step);
      if (session && session.ws) {
        appendMessage("system", t("status.workflow_changed_hint"));
      }
      // If recorder is armed, refresh the captured workflow id so the
      // recorded archive's manifest reflects the user's current pick.
      if (recorder && recorder.isWorkflowArmed()) {
        try {
          recorder.armWorkflowCapture({ workflowId: selectedWorkflowId() });
        } catch (_) {
          /* re-arm is best-effort */
        }
      }
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        await handleAuthLost();
        return;
      }
      console.warn("could not load selected workflow", e);
    }
  });
}

if (els.languageSelect) {
  // Initialise the select to the persisted language. Listeners below react
  // to the user changing it; the dropdown options themselves are static.
  els.languageSelect.value = getLanguage();
  els.languageSelect.addEventListener("change", async () => {
    const requested = els.languageSelect.value;
    if (!SUPPORTED_LANGUAGES.includes(requested)) return;
    setLanguage(requested);
    // Re-fetch the currently-selected workflow with the new language so the
    // step card / next-step preview show localised text immediately. If the
    // user is not yet signed in (workflowSel empty) this is a no-op.
    if (els.workflowSel && els.workflowSel.value) {
      try {
        const workflowCfg = await fetchWorkflow(selectedWorkflowId());
        if (workflowCfg.workflow) {
          ensureSession().setWorkflowConfig(workflowCfg.workflow);
          void preloadWorkflowImagesInOrder(workflowCfg.workflow);
        }
        if (workflowCfg.active_step) renderWorkflowState(workflowCfg.active_step);
      } catch (e) {
        if (e instanceof UnauthorizedError) {
          await handleAuthLost();
          return;
        }
        console.warn("could not re-load workflow for language change", e);
      }
    }
    // If a live session is already running, the model prompt was built in
    // the prior language. A reconnect is required to switch the model side.
    if (session && session.ws) {
      appendMessage("system", t("status.lang_changed_hint"));
    }
  });
}

// Apply the i18n catalog once on first load, and again on every
// language change so static labels stay in sync.
onLanguageChange(() => applyStaticI18n());

ensureSession();

if (window.ResizeObserver && els.currentStepCard) {
  const ro = new ResizeObserver(() => scheduleCurrentStepFit());
  ro.observe(els.currentStepCard);
  if (els.nextStepPreview) ro.observe(els.nextStepPreview);
  if (els.nextStepContent) ro.observe(els.nextStepContent);
}

window.addEventListener("resize", () => scheduleCurrentStepFit());

applyStaticI18n();
bootstrap();
setState("idle", t("status.disconnected"));
setDebugWsState("closed");
setWorkflowControlsEnabled(false);
updateDebugCounters();
logDebug("browser", "app_loaded", {
  send_rate: SEND_RATE,
  receive_rate: RECV_RATE,
  frame_interval_ms: FRAME_INTERVAL_MS,
  frame_max_dim: FRAME_MAX_DIM,
  auto_check_interval_ms: AUTO_CHECK_INTERVAL_MS,
});
